import {formatCount} from "core/format/countFormat";
import {minorToMajor} from "core/format/minorUnits";
import type {Locale} from "i18n/localePreference";
import {formatMoney} from "pricing/PricingStorage";
import type {ReferralSummary} from "./ReferralApi";
import {referralShareLink, type ReferralShareLink} from "./referralLinks";
import type {ReferrerRewardStory} from "./referrerReward";

/** Everything the "Refer a friend" card renders. Presentation-ready: the view does no arithmetic. */
export type ReferralCardModel = {
	code: string;
	pendingCount: string;
	vestedCount: string;
	/** Formatted credit (e.g. `"€19.98"`), or null when nothing has vested yet. */
	earnedCredit: string | null;
	hasEarned: boolean;
	capReached: boolean;
	shareLink: ReferralShareLink | null;
	/**
	 * Which of the referrer's two rewards this reader's copy names (ui#1693).
	 *
	 * It rides the card model rather than being read at each view, so the account card and the success
	 * line — which share this model through `useReferralCard` — cannot describe the same programme
	 * differently to the same person on two screens.
	 */
	rewardStory: ReferrerRewardStory;
};

/**
 * Turns the server's referral summary into the card's view model
 * (kitecorp/transferify-api-service#433).
 *
 * React-free by mandate (epic #26). The two rules worth keeping out of JSX:
 * <ul>
 *   <li>the server speaks MINOR currency units (what Stripe's balance API takes), so the conversion —
 *       {@link core/format/minorUnits}, which reads each currency's own exponent back from ICU instead
 *       of hardcoding a `/ 100` that a zero-decimal currency would render 100× too high — happens here
 *       once rather than in every place a number is shown;</li>
 *   <li>"nothing earned yet" renders as NO credit line, not as a formatted zero — "€0.00" reads as a
 *       balance that will always be empty, which is the opposite of the card's job.</li>
 * </ul>
 *
 * `currentOrigin` is the browser's own (ui#1517): the card exposes only that site's link, so a site
 * never advertises another brand and adding a brand requires no referral-card change. Empty before
 * the hook has mounted, which {@link referralShareLink} answers with no link.
 *
 * `rewardStory` is passed in ALREADY DECIDED by `referral/referrerReward.ts` rather than derived from
 * a plan here (ui#1693). It is the one input that describes the READER rather than the programme, and
 * keeping its rule in one module is what stops a second reading of "is this account really paying"
 * from growing next to the one the api itself uses.
 */
export function referralCardModel(
	summary: ReferralSummary,
	locale: Locale,
	currentOrigin: string | null | undefined,
	rewardStory: ReferrerRewardStory,
): ReferralCardModel {
	const hasEarned = summary.earnedCreditMinor > 0 && !!summary.earnedCreditCurrency;
	return {
		code: summary.code,
		pendingCount: formatCount(summary.pendingCount, locale),
		vestedCount: formatCount(summary.vestedCount, locale),
		earnedCredit: hasEarned
			? formatMoney(
				summary.earnedCreditCurrency as string,
				minorToMajor(summary.earnedCreditMinor, summary.earnedCreditCurrency as string),
				locale,
			)
			: null,
		hasEarned,
		capReached: summary.remainingThisYear <= 0,
		shareLink: referralShareLink(summary.code, currentOrigin),
		rewardStory,
	};
}
